import * as d3 from 'd3';
import { DecisionBlock, BlockGeometry, EditMode, Block, Position } from '../../types';
import { STYLES } from '../../styles/constants';
import { implementDrag } from '../utils/dragUtils';
import { implementResize } from '../utils/resizeUtils';
import { setupTextEditing } from '../../utils/textEditor';
import { getBlockDimensions, getBlockFill } from '../../utils/blockUtils';
import { appendBpmnShape } from '../../utils/bpmnShapeRenderer';
import { isBpmnSymbolId } from '../../../data/bpmnSymbols';
import { layoutDecisionDiamondText } from '../../utils/decisionLabelLayout';
import { appendBpmnMeaningCaption } from '../../utils/bpmnMeaningCaption';

export function createDecisionBlock(
  container: d3.Selection<HTMLDivElement, unknown, null, undefined>,
  decision: DecisionBlock,
  editMode: EditMode,
  onDragEnd: (id: string, position: BlockGeometry) => void,
  onTextUpdate: (id: string, newText: string) => void,
  allBlocks: Block[] = [],
  t: (key: string) => string,
  setBlocks: React.Dispatch<React.SetStateAction<Block[]>>,
  onDragMove?: (updates: Array<{ id: string; position: Position }>) => void,
  getSelectedBlockIds?: () => string[],
  onResizeMove?: (id: string, geometry: BlockGeometry) => void
) {
  const dimensions = getBlockDimensions(decision);
  const fill = getBlockFill(decision);
  const halfWidth = dimensions.width / 2;
  const halfHeight = dimensions.height / 2;
  

  const blockContainer = container
    .append('div')
    .attr('class', `decision-block-container-${decision.id} block`)
    .style('position', 'absolute')
    .style('left', `${decision.x || 0}px`)
    .style('top', `${decision.y || 0}px`)
    .style('z-index', '2');
  ;

  const block = blockContainer
    .append('div')
    .attr('class', `decision-block-${decision.id}`)
    .style('width', `${dimensions.width}px`)
    .style('height', `${dimensions.height}px`)
    .style('cursor', editMode.enabled ? 'move' : 'default')
    .style('user-select', 'none')
    .style('display', 'flex')
    .style('align-items', 'center')
    .style('justify-content', 'center')
    .style('position', 'relative');

  const svg = block
    .append('svg')
    .attr('width', dimensions.width)
    .attr('height', dimensions.height)
    .style('overflow', 'visible');

  if (decision.bpmnSymbol && isBpmnSymbolId(decision.bpmnSymbol)) {
    appendBpmnShape(svg, decision.bpmnSymbol, {
      width: dimensions.width,
      height: dimensions.height,
      fill,
      stroke: STYLES.colors.border,
    });
    appendBpmnMeaningCaption(
      blockContainer,
      decision,
      dimensions.height,
      dimensions.width,
      editMode,
      onTextUpdate,
    );
  } else {
    svg
      .append('path')
      .attr('d', `M ${halfWidth} 0 L ${dimensions.width} ${halfHeight} L ${halfWidth} ${dimensions.height} L 0 ${halfHeight} Z`)
      .style('fill', fill)
      .style('stroke', STYLES.colors.border)
      .style('stroke-width', '1px');

    const textElement = svg.append('text')
      .attr('class', 'decision-block-label')
      .attr('text-anchor', 'middle')
      .attr('dominant-baseline', 'middle')
      .style('fill', decision.textStyle?.textColor || STYLES.colors.getBlockColors('process').text)
      .style('font-weight', decision.textStyle?.fontWeight || 'normal')
      .style('font-family', decision.textStyle?.fontFamily || 'inherit');

    layoutDecisionDiamondText(svg, decision, dimensions.width, dimensions.height);

    const textElementForEditing = textElement.node();
    if (textElementForEditing instanceof SVGTextElement) {
      setupTextEditing(textElementForEditing, decision, editMode.enabled, onTextUpdate);
    }
  }

  if (editMode.enabled) {
    implementDrag(blockContainer, decision.id, allBlocks, onDragEnd, setBlocks, onDragMove, getSelectedBlockIds);
    implementResize(blockContainer, decision, onDragEnd, setBlocks, onResizeMove);
  }

  return blockContainer;
}